import moment from 'moment-timezone';

/**
 * Helper function to manage state for DateTimePicker and DateTimePickerModal.
 */
export function useDatePickerDateAndTime(
  value: Date | undefined,
  timeZoneId: string,
  onChange: ((date: Date | undefined) => void) | undefined,
  options: {
    hideSeconds?: boolean;
    disallowFuture?: boolean;
    minTime?: number | Date;
    maxTime?: number | Date;
    onBlur?: () => void;
  },
): {
  date: string | undefined;
  time: string | undefined;
  setDate: (date: string | undefined) => void;
  setTime: (time: string | undefined) => void;
  onBlur: () => void;
} {
  const { hideSeconds, disallowFuture, minTime, maxTime, onBlur: originalOnBlur } = options;
  const [date, time] = dateToDayAndTime(value, timeZoneId, hideSeconds);

  const validUpdate = (date: Date | undefined) => {
    return (date === undefined || !isNaN(date.valueOf())) && date?.valueOf() !== value?.valueOf();
  };

  const updateDate = (date: string | undefined) => {
    const newDate = dayAndTimeToDate(date, time ?? '00:00', timeZoneId);
    if (validUpdate(newDate)) {
      onChange?.(newDate);
    }
  };

  const updateTime = (time: string | undefined) => {
    const newDate = dayAndTimeToDate(date ?? currentDay(timeZoneId), time, timeZoneId);
    if (validUpdate(newDate)) {
      onChange?.(newDate);
    }
  };

  const onBlur = () => {
    originalOnBlur?.();

    let newDate = value;
    if (newDate && minTime && newDate < minTime) {
      newDate = new Date(minTime);
    }
    if (newDate && disallowFuture && newDate.valueOf() > Date.now()) {
      newDate = new Date();
    }
    if (newDate && maxTime && newDate > maxTime) {
      newDate = new Date(maxTime);
    }

    if (newDate?.valueOf() !== value?.valueOf()) {
      onChange?.(newDate);
    }
  };

  return {
    date,
    time,
    setDate: updateDate,
    setTime: updateTime,
    onBlur,
  };
}

/**
 * Convert a date to an ISO8601 day string.
 *
 * @param date the date to convert.  This can be a date from any point in time during the specified day.
 */
export function dateToDay(date: Date | number, timeZone: string): string;
export function dateToDay(date: Date | number | undefined, timeZone: string): string | undefined;
export function dateToDay(date: Date | number | undefined, timeZone: string): string | undefined {
  return moment.tz(date, timeZone).format('YYYY-MM-DD');
}

/**
 * Returns the current date in ISO8601 format.
 */
export function currentDay(timeZone: string): string {
  return dateToDay(new Date(), timeZone);
}

/**
 * Convert a day to a date.  This always returns the start of the day.
 */
export function dayToDate(day: string, timeZone: string): Date;
export function dayToDate(day: string | undefined, timeZone: string): Date | undefined;
export function dayToDate(day: string | undefined, timeZone: string): Date | undefined {
  return moment.tz(day, timeZone).toDate();
}

/**
 * Convert a day to a date.  This always returns the end of the day.
 */
export function endOfDayToDate(day: string, timeZone: string): Date;
export function endOfDayToDate(day: string | undefined, timeZone: string): Date | undefined;
export function endOfDayToDate(day: string | undefined, timeZone: string): Date | undefined {
  if (day === undefined) {
    return undefined;
  }
  return moment.tz(day, timeZone).endOf('day').toDate();
}

function dayAndTimeToDate(
  date: string | undefined,
  time: string | undefined,
  timeZoneId: string,
): Date | undefined {
  if (date === undefined || time === undefined) {
    return undefined;
  }

  return moment.tz(`${date}T${time}`, timeZoneId).toDate();
}

export function dateToDayAndTime(
  jsDate: Date | number | undefined,
  timeZoneId: string,
  hideSeconds?: boolean,
): [string | undefined, string | undefined] {
  if (jsDate === undefined) {
    return [undefined, undefined];
  }
  return [
    moment.tz(jsDate, timeZoneId).format('YYYY-MM-DD'),
    moment.tz(jsDate, timeZoneId).format(hideSeconds ? 'HH:mm' : 'HH:mm:ss'),
  ];
}

/**
 * Helper function for converting a day string (e.g. "2022-10-31") into a Date.
 * The returned date is the start of the day in the local timezone.
 */
export function dayToLocalDate(day: string): Date;
export function dayToLocalDate(day: string | undefined): Date | undefined;
export function dayToLocalDate(day: string | undefined): Date | undefined {
  if (day === undefined) {
    return undefined;
  }
  const [yy, mm, dd] = day.split('-').map((v) => parseInt(v, 10));
  return new Date(yy, mm - 1, dd, 0, 0, 0);
}
